import { ChangeDetectorRef, ElementRef, EventEmitter, Renderer2, SimpleChanges, } from '@angular/core';
import { Store } from '@ngrx/store';
import Highcharts from 'highcharts/highstock';
import isEmpty from 'lodash.isempty';
import { Subscription } from 'rxjs';
import { setFundChartSecurities } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/securities/securities.actions';
import { ADDITIONAL_LEGEND_HEIGHT, BASE_LEGEND_HEIGHT, EVENT_LABEL_HTML, HIGHCHARTS_FUND_DEFAULT_OPTIONS, TOOLTIP_STYLE_BOX, } from '../../constants';
import { ChartZoomService } from 'ChartApp/ts/features/charts-shared/services/chart-zoom.service';
import { FundChartService } from 'ChartApp/ts/services/fund-chart.service';
import { observeContainerResize } from 'ChartApp/ts/utils/chart-container-resize';
import { isSameDateUTC } from 'NewAngular/utils/date';
import deepEqual from 'NewAngular/utils/equal/deep-equal';
import { isMobile } from 'NewAngular/utils/media';
import YchartFundInteractive from 'ChartApp/ts/features/fundamental-chart/ychart_fund_interactive';
import * as i0 from "@angular/core";
import * as i1 from "ChartApp/ts/services/fund-chart.service";
import * as i2 from "ChartApp/ts/features/charts-shared/services/chart-zoom.service";
import * as i3 from "@ngrx/store";
import * as i4 from "ChartApp/ts/features/charts-shared/components/chart-zoom-controls/chart-zoom-controls.component";
const _c0 = ["chart"];
export class FundamentalChartComponent {
    constructor(fundChartService, renderer, eltRef, changeDetector, chartZoomService, store) {
        this.fundChartService = fundChartService;
        this.renderer = renderer;
        this.eltRef = eltRef;
        this.changeDetector = changeDetector;
        this.chartZoomService = chartZoomService;
        this.store = store;
        this.axisValue = {};
        this.chartId = '';
        this.chartDataUrl = '';
        this.chartFormData = {};
        this.chartJsonData = {};
        this.chartAnnotations = [];
        this.annotationsHidden = false;
        this.noDataClass = '';
        this.noDataHtml = '';
        this.sidebarOpen = '';
        this.chartOptions = {};
        this.showEmptyChart = false;
        this.hideZoomControls = false;
        this.aISummaryKeys = [];
        this.chartDataHandler = new EventEmitter(true);
        this.subchartOffsetsHandler = new EventEmitter(true);
        this.tooltipDataHandler = new EventEmitter(true);
        this.annotationHandler = new EventEmitter(true);
        this.toolsetToggleHandler = new EventEmitter(true);
        this.chartClickHandler = new EventEmitter();
        this.openAISummaryClickEvent = new EventEmitter();
        this.annotationEventsEnabled = true;
        this.resizeEventBound = false;
        this.resizeObserver = null;
        this.noDataMessage = '';
        this.getStartedMessage = '';
        this.chartResponse = null;
        this.REBIND_CHART_RESIZE_EVENT = 'rebindChartResize';
        this.fetchSubscription = null;
        this.chartZoomSubscription = null;
        this.mouseOverChart = false;
        this.chartLocalId = this.chartId ? this.chartId : String(Math.random());
        this.mouseCoords = null;
        this.panningOn = false;
        this.zoomOnSubscription = new Subscription();
        this.chartClasses = '';
        this.chartStyles = '';
        this._hideZoomControls = false;
        this.annotationEventCallback = (event) => {
            if (!this.annotationEventsEnabled) {
                return;
            }
            // @ts-ignore
            const { chart, userOptions, options } = event.target;
            const id = chart.annotations.indexOf(event.target);
            let clicksToCompleteAnnotation = 1;
            // This is probably a bug in highcharts, but when the height
            // of a parallelChannel annotation is changed,
            // the userOptions object is not updated with the new height.
            // We need to get the new height from the event here and add it to the
            // userOptions object before emitting from this.annotationHandler.
            if (options.langKey === 'parallelChannel') {
                userOptions.typeOptions.height = options.typeOptions.height;
                clicksToCompleteAnnotation = 2;
            }
            this.annotationHandler.emit({
                chartAnnotation: userOptions,
                id,
            });
            // Some line and shape annotations take multiple clicks to complete their
            // placement on the chart, but after the first click Highcharts starts
            // firing 'afterUpdate' events constantly until the annotation placement is finished.
            // Handling all of these events causes a big performance drop, and we only need to update the
            // state once after the annotation placement is complete. So after an 'add' event,
            // we ignore other annotation events until the required number of clicks
            // to place the annotation have occurred.
            if (event.type === 'add') {
                this.annotationEventsEnabled = false;
                let clicksAfterAdd = 0;
                const enableEventsCallback = () => {
                    clicksAfterAdd += 1;
                    if (clicksAfterAdd >= clicksToCompleteAnnotation) {
                        this.annotationEventsEnabled = true;
                        this.hostElement.removeEventListener('mousedown', enableEventsCallback);
                    }
                };
                this.hostElement.addEventListener('mousedown', enableEventsCallback);
            }
        };
        this.resizeHandler = () => {
            // Container-driven resizes are handled by the ResizeObserver (which calls setSize on the live chart).
            // Dynamic-height charts size from the window, so they still need a window-driven redraw.
            if (this.configurableOptions.dynamicHeight) {
                this.redrawChart();
            }
        };
        this.hostElement = this.eltRef.nativeElement;
        this.defaultOptions = HIGHCHARTS_FUND_DEFAULT_OPTIONS;
    }
    ngOnInit() {
        this.setupRebindResizeHandler();
        this.zoomOnSubscription = this.chartZoomService.getZoomEvent().subscribe(({ panningOn }) => {
            this.panningOn = panningOn;
        });
    }
    displayChartLoader() {
        this.renderer.addClass(this.hostElement, this.noDataClass);
        this.chartElement.nativeElement.innerHTML =
            '<div class="loader"><div class="loader-message">Loading ...</div><div class="loader-spinner"></div></div>';
        this.unbindResizeHandler();
    }
    ngOnChanges(changes) {
        if (changes.chartId) {
            const chartIdName = this.chartId === '' ? String(Math.random()) : this.chartId;
            this.chartLocalId = `${chartIdName}s1`;
        }
        if (changes.noDataHtml)
            this.noDataMessage = changes.noDataHtml.currentValue;
        this.noDataMessage = this.noDataHtml;
        if (!this.renderElement) {
            this.renderElement = this.hostElement.parentElement;
        }
        if (this.chartElement) {
            if (this.defaultOptions?.chart) {
                this.defaultOptions.chart.renderTo = this.chartElement.nativeElement;
            }
        }
        const dataSource = changes.chartDataUrl?.currentValue ? changes.chartDataUrl : changes.chartFormData;
        if (dataSource?.currentValue && !deepEqual(dataSource.currentValue, dataSource.previousValue)) {
            this._hideZoomControls = true;
            if (this.fetchSubscription !== null) {
                this.fetchSubscription.unsubscribe();
            }
            this.fetchSubscription = this.getChartData().subscribe({
                next: (rsp) => {
                    // Set chartResponse for the resize handler
                    this.chartResponse = rsp;
                    this.store.dispatch(setFundChartSecurities({ fundChartSecurities: rsp.params.securities }));
                    if (this.chartDataHandler)
                        this.chartDataHandler.emit({});
                },
                error: (err) => {
                    this.displayDataError(err);
                    this._hideZoomControls = false;
                },
                complete: () => {
                    // Set chartResponse for the resize handler
                    this.renderChart(this.chartResponse);
                    this._hideZoomControls = this.hideZoomControls;
                },
            });
        }
        else if (dataSource && !dataSource.currentValue) {
            this.resetChart();
            this.displayNoData();
            if (this.chartDataHandler)
                this.chartDataHandler.emit({});
        }
        if (changes.chartJsonData && changes.chartJsonData.currentValue.chart_data) {
            this.renderChart(changes.chartJsonData.currentValue);
        }
        if (changes.sidebarOpen && changes.sidebarOpen.currentValue !== undefined) {
            setTimeout(() => {
                this.redrawChart();
            }, 200);
        }
        if (changes.chartOptions?.currentValue &&
            !deepEqual(changes.chartOptions.currentValue, changes.chartOptions.previousValue)) {
            // only redraw the chart if a chart is currently displayed
            if (this.chartDataUrl || Object.keys(this.chartFormData ?? {}).length)
                this.redrawChart();
        }
        if (changes.annotationsHidden) {
            this.redrawChart();
        }
    }
    ngOnDestroy() {
        this.unbindResizeHandler();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.fetchSubscription !== null) {
            this.fetchSubscription.unsubscribe();
        }
        if (this.chartZoomSubscription !== null) {
            this.chartZoomSubscription.unsubscribe();
        }
        this.zoomOnSubscription.unsubscribe();
    }
    deleteAnnotation(id) {
        if (this.ychartFundInteractive?.stockChart && this.ychartFundInteractive?.stockChart.annotations.length) {
            this.ychartFundInteractive.stockChart.navigationBindings.deselectAnnotation();
            this.ychartFundInteractive.stockChart.annotations[id].destroy();
            this.ychartFundInteractive.stockChart.annotations.splice(id, 1);
        }
    }
    updateAnnotation(annotation, id) {
        if (this.ychartFundInteractive?.stockChart) {
            this.annotationEventsEnabled = false;
            this.ychartFundInteractive.stockChart.annotations[id].update(annotation);
            this.annotationEventsEnabled = true;
        }
    }
    getChartData() {
        this.resetChart();
        this.displayChartLoader();
        if (this.chartDataHandler)
            this.chartDataHandler.emit({});
        const { chartDataUrl, chartFormData } = this;
        const maxPoints = parseInt(String(this.renderElement.clientWidth * 0.9), 10);
        if (chartFormData && !isEmpty(chartFormData)) {
            const chartDataPostParams = maxPoints !== 0 ? { ...chartFormData, maxPoints } : chartFormData;
            return this.fundChartService.getChartDataFromState(chartDataPostParams);
        }
        const chartDataGetParams = maxPoints !== 0 ? `${chartDataUrl}&maxPoints=${maxPoints}` : chartDataUrl;
        return this.fundChartService.getChartDataFromUrl(chartDataGetParams);
    }
    displayNoDataLabel() {
        if (this.noDataMessage === '') {
            this.noDataMessage = `<span>No data to display.</span>`;
        }
        this.displayNoData();
    }
    resetChart() {
        // Remove tooltip and offset data
        this.tooltipDataHandler.emit([]);
        this.subchartOffsetsHandler.emit([]);
        // Manually run change detection so all components are updated with blank tooltip and
        // offset data. An ExpressionChangedAfterItHasBeenCheckedError will be thrown otherwise.
        this.changeDetector.detectChanges();
        // Always destroy the chart if it exists
        if (this.ychartFundInteractive)
            this.ychartFundInteractive.destroyStockChart();
    }
    displayNoData() {
        // Remove chartData so that if the chart wasn't cleaned up nicely,
        // it doesn't reappear
        this.renderer.addClass(this.hostElement, this.noDataClass);
        this.chartClasses = '';
        this.chartStyles = '';
        this.chartElement.nativeElement.innerHTML = this.noDataMessage;
        this.toolsetToggleHandler.emit(false);
        this.unbindResizeHandler();
        this.chartZoomService.clearChart();
    }
    displayDataError(response) {
        // // If we get an error due to a bad request, write the error message
        if (response.error && response.status === 400) {
            this.noDataMessage = `<span>${response.error.error}</span>`;
        }
        else if (response.error && response.status === 404) {
            this.noDataMessage = `<span>Chart not found.</span>`;
        }
        else {
            this.noDataMessage = this.getStartedMessage;
        }
        this.displayNoData();
    }
    clearChartContainer() {
        this.renderer.removeClass(this.hostElement, this.noDataClass);
        this.chartClasses = '';
        this.chartStyles = '';
        this.chartElement.nativeElement.innerHTML = '';
    }
    redrawChart() {
        setTimeout(() => {
            this.renderChart(this.chartResponse);
            this.windowWidth = window.innerWidth;
        });
    }
    setEmptyChart(chartInfo) {
        // Hide the latest value label on the y-axis
        this.configurableOptions.showLatestValueLabels = false;
        // Add fake data so that the x-axis appears
        if (chartInfo?.chart_data[0] && chartInfo?.chart_data[0][0] && chartInfo.start_date) {
            const startDate = new Date(chartInfo.start_date);
            startDate.setMinutes(-startDate.getTimezoneOffset());
            const endDate = chartInfo.end_date ? new Date(chartInfo.end_date) : new Date();
            endDate.setHours(0, 0, 0, 0);
            endDate.setMinutes(-endDate.getTimezoneOffset());
            // eslint-disable-next-line no-param-reassign
            chartInfo.chart_data[0][0].raw_data = [
                [startDate.getTime(), 999],
                [endDate.getTime(), 999],
            ];
            for (let i = 1; i < chartInfo.chart_data[0].length; i += 1) {
                // eslint-disable-next-line no-param-reassign
                chartInfo.chart_data[0][i].raw_data = [];
            }
        }
    }
    renderChart(response) {
        if (!response)
            return;
        this.chartResponse = response;
        const chartInfo = response;
        let { chartOptions } = this;
        if (this.showEmptyChart) {
            this.setEmptyChart(chartInfo);
            // Set y-axis values
            chartOptions = {
                yAxis: {
                    min: 10,
                    max: 100,
                },
            };
        }
        // Set our highchart options and configurable options with deep copies
        this.highchartOptions = Highcharts.merge(HIGHCHARTS_FUND_DEFAULT_OPTIONS, chartOptions);
        this.highchartOptions = Highcharts.merge(this.highchartOptions, {
            navigation: {
                annotationsOptions: {
                    events: {
                        add: this.annotationEventCallback,
                        afterUpdate: this.annotationEventCallback,
                    },
                },
            },
        });
        this.setHighchartsAISummaryEvents();
        const adjustLegend = isMobile() &&
            this.configurableOptions.tooltipStyle !== TOOLTIP_STYLE_BOX &&
            this.configurableOptions.dynamicHeight;
        // get chartDimensions using window information
        const chartDimensions = this.getChartDimensions(chartInfo.chart_data, adjustLegend);
        const numCharts = chartInfo.chart_data.length;
        if (numCharts > 0) {
            if (this.chartDataHandler) {
                this.chartDataHandler.emit(chartInfo);
            }
            this.resetChart();
            this.clearChartContainer();
            if (this.fundChartService.chartHasNoData(chartInfo)) {
                this.displayNoDataLabel();
                return;
            }
            this.setTooltipData();
            this.highchartOptions = this.chartZoomService.addZoomOptions(this.highchartOptions);
            this.ychartFundInteractive = new YchartFundInteractive({ ...this.highchartOptions }, { ...this.configurableOptions, adjustLegend });
            this.chartClasses = 'flex-auto full-height';
            this.chartStyles = 'height: 0; overflow: hidden;';
            // Don't attempt to render the chart if the chart element is not in the DOM
            if (this.ychartFundInteractive && document.getElementById(this.chartLocalId)) {
                this.annotationEventsEnabled = false;
                const annotationsToAdd = this.annotationsHidden
                    ? []
                    : this.chartAnnotations.map((annotation) => {
                        return {
                            ...annotation,
                            events: {
                                add: this.annotationEventCallback,
                                afterUpdate: this
                                    .annotationEventCallback,
                            },
                        };
                    });
                this.ychartFundInteractive.createStockChart(this.chartLocalId, chartInfo, this.chartElement.nativeElement, chartDimensions, annotationsToAdd);
                if (this.ychartFundInteractive.stockChart) {
                    this.chartZoomService.setChart(this.ychartFundInteractive.stockChart);
                    if (this.axisValue) {
                        this.chartZoomService.zoomIn(this.axisValue);
                    }
                }
                this.annotationEventsEnabled = true;
                // Update subchartOffsets to be passed to tooltip component from parent
                this.subchartOffsetsHandler.emit(this.ychartFundInteractive.subchartOffsets);
            }
            this.bindResizeHandler();
            this.setupResizeObserver();
            this.toolsetToggleHandler.emit(true);
        }
        else {
            this.displayNoData();
            if (this.chartDataHandler)
                this.chartDataHandler.emit({});
        }
    }
    getChartDimensions(chartObjects, adjustLegend) {
        const chartWidth = this.renderElement.clientWidth;
        let chartHeight;
        if (this.configurableOptions.dynamicHeight) {
            const winHeight = window.innerHeight;
            const chartOffset = this.renderElement.getBoundingClientRect().top;
            // the chartHeight is total window height, minus everything on top
            // and 10px padding on bottom, and 28px footer.
            chartHeight = Math.max(winHeight - chartOffset - 38, this.hostElement.offsetHeight);
        }
        else {
            chartHeight = this.renderElement.getBoundingClientRect().height;
        }
        // If we are in adjustLegend mode (legend between charts) we need to add extra height
        // that the legend takes up
        let chartMaxHeight;
        if (adjustLegend) {
            let extraHeight = 0;
            for (let i = 0; i < chartObjects.length; i += 1) {
                for (let j = 0; j < chartObjects[i].length; j += 1) {
                    if (j === 0) {
                        extraHeight += BASE_LEGEND_HEIGHT;
                    }
                    else {
                        extraHeight += ADDITIONAL_LEGEND_HEIGHT;
                    }
                }
            }
            chartMaxHeight = Math.round(1.2 * chartWidth) + extraHeight;
        }
        else {
            chartMaxHeight = Math.round(0.9 * chartWidth);
        }
        return {
            height: chartHeight,
            maxHeight: chartMaxHeight,
            width: chartWidth,
        };
    }
    setTooltipData() {
        // We use this when we want a callback to a custom tooltip directive
        // Sets up a tooltipData array to be populated by Highchart on mouseover
        this.configurableOptions.tooltipCallback = (tooltipData) => {
            setTimeout(() => {
                if (this.ychartFundInteractive?.stockChart?.tooltip) {
                    this.tooltipDataHandler.emit(tooltipData);
                }
            });
        };
    }
    setupRebindResizeHandler() {
        // listen for other charts instructing this chart to re-bind itself to the resize event
        window.addEventListener(this.REBIND_CHART_RESIZE_EVENT, () => {
            if (this.resizeEventBound) {
                this.bindResizeHandler(true);
            }
        });
    }
    /**
     * Set up a ResizeObserver for the host element, so we can dynamically resize the chart
     * when the size of the host element changes.
     * @private
     */
    setupResizeObserver() {
        if (this.resizeObserver || !this.renderElement) {
            return;
        }
        this.resizeObserver = observeContainerResize(this.renderElement, 
        // Read the chart at fire time so we always size the current instance.
        (width, height) => this.ychartFundInteractive?.stockChart?.setSize(width, height, false), 
        // Dynamic-height charts size from the window, so they keep using the window resize handler.
        { skip: () => this.configurableOptions.dynamicHeight });
    }
    bindResizeHandler(isRebinding = false) {
        // Calling `.unbind` will cause all of the charts to unbind from the "resize" event.
        // In that case, we have to rebind all of the other charts on the page, which is accomplished by setting
        // `isRebinding=true`.
        if (isRebinding || !this.resizeEventBound) {
            window.addEventListener('resize', this.resizeHandler);
            this.resizeEventBound = true;
        }
    }
    unbindResizeHandler() {
        if (this.resizeEventBound) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeEventBound = false;
            // calling unbind above will cause all charts in the window to unbind from their resize handlers
            // we must instruct them to re-bind
            // eslint-disable-next-line no-undef
            window.dispatchEvent(new Event(this.REBIND_CHART_RESIZE_EVENT));
        }
    }
    setMouseDownCoords(event) {
        this.mouseCoords = {
            x: event.clientX,
            y: event.clientY,
        };
    }
    handleClickOrDrag(event) {
        if (!this.panningOn && this.mouseCoords) {
            const { x, y } = this.mouseCoords;
            const mouseUpX = event.clientX;
            const mouseUpY = event.clientY;
            const areCoordsInClickRange = (mouseDown, mouseUp) => Boolean(Math.abs(mouseDown - mouseUp) < 3);
            const isClick = areCoordsInClickRange(x, mouseUpX) && areCoordsInClickRange(y, mouseUpY);
            if (isClick) {
                this.chartClickHandler.emit();
            }
        }
    }
    setHighchartsAISummaryEvents() {
        const that = this;
        this.highchartOptions = Highcharts.merge(this.highchartOptions, {
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        formatter() {
                            const pointDate = new Date(this.key);
                            const seriesId = this.series.options.id || this.series.options.onSeries;
                            const lastUnderscoreIdx = seriesId.lastIndexOf('_');
                            const calc = seriesId.substring(0, lastUnderscoreIdx);
                            const securityId = seriesId.substring(lastUnderscoreIdx + 1);
                            const seriesPointExists = that.aISummaryKeys.find((aiSummary) => {
                                const currentDate = new Date(aiSummary.startDate);
                                return (isSameDateUTC(pointDate, currentDate) &&
                                    securityId === aiSummary.securityId &&
                                    calc === aiSummary.calc);
                            });
                            return seriesPointExists ? EVENT_LABEL_HTML : undefined;
                        },
                    },
                },
            },
            chart: {
                events: {
                    render: (event) => {
                        const { series } = event.target;
                        const clickEventFunction = (point) => (e) => {
                            const data = {
                                selectedStartDate: new Date(point.key || point.x),
                                seriesId: point.series.options.id,
                            };
                            this.openAISummaryClickEvent.emit(data);
                            e.stopPropagation();
                        };
                        for (let i = 0; i < series.length; i += 1) {
                            const { points } = series[i];
                            points.forEach((point) => {
                                if (point.dataLabel) {
                                    point.dataLabel.div.addEventListener('click', clickEventFunction(point));
                                }
                            });
                        }
                    },
                },
            },
        });
    }
}
FundamentalChartComponent.ɵfac = function FundamentalChartComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FundamentalChartComponent)(i0.ɵɵdirectiveInject(i1.FundChartService), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i2.ChartZoomService), i0.ɵɵdirectiveInject(i3.Store)); };
FundamentalChartComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: FundamentalChartComponent, selectors: [["ycn-fundamental-chart"]], viewQuery: function FundamentalChartComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.chartElement = _t.first);
    } }, inputs: { axisValue: "axisValue", chartId: "chartId", chartDataUrl: "chartDataUrl", chartFormData: "chartFormData", chartJsonData: "chartJsonData", chartAnnotations: "chartAnnotations", annotationsHidden: "annotationsHidden", noDataClass: "noDataClass", noDataHtml: "noDataHtml", sidebarOpen: "sidebarOpen", chartOptions: "chartOptions", configurableOptions: "configurableOptions", showEmptyChart: "showEmptyChart", hideZoomControls: "hideZoomControls", aISummaryKeys: "aISummaryKeys" }, outputs: { chartDataHandler: "chartDataHandler", subchartOffsetsHandler: "subchartOffsetsHandler", tooltipDataHandler: "tooltipDataHandler", annotationHandler: "annotationHandler", toolsetToggleHandler: "toolsetToggleHandler", chartClickHandler: "chartClickHandler", openAISummaryClickEvent: "openAISummaryClickEvent" }, standalone: false, features: [i0.ɵɵProvidersFeature([ChartZoomService]), i0.ɵɵNgOnChangesFeature], decls: 3, vars: 7, consts: [["chart", ""], [3, "hidden", "mouseOverChart"], [3, "mouseup", "mousedown", "mouseenter", "mouseleave", "id"]], template: function FundamentalChartComponent_Template(rf, ctx) { if (rf & 1) {
        const _r1 = i0.ɵɵgetCurrentView();
        i0.ɵɵelement(0, "ycn-chart-zoom-controls", 1);
        i0.ɵɵelementStart(1, "div", 2, 0);
        i0.ɵɵlistener("mouseup", function FundamentalChartComponent_Template_div_mouseup_1_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleClickOrDrag($event)); })("mousedown", function FundamentalChartComponent_Template_div_mousedown_1_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.setMouseDownCoords($event)); })("mouseenter", function FundamentalChartComponent_Template_div_mouseenter_1_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.mouseOverChart = true); })("mouseleave", function FundamentalChartComponent_Template_div_mouseleave_1_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.mouseOverChart = false); });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("hidden", ctx._hideZoomControls)("mouseOverChart", ctx.mouseOverChart);
        i0.ɵɵadvance();
        i0.ɵɵstyleMap(ctx.chartStyles);
        i0.ɵɵclassMap(ctx.chartClasses);
        i0.ɵɵproperty("id", ctx.chartLocalId);
    } }, dependencies: [i4.ChartZoomControlsComponent], encapsulation: 2 });
